/**
 * Swarm drones (TRD-06 §5).
 *
 * A third entity type deliberately: not enemies, because nothing targets them, and not
 * towers, because they move and expire. Giving them their own pool keeps both of those
 * loops free of "is this one actually a drone" branches.
 */
import { DT } from '../constants.js';
import type { Drone, Enemy, EntityPool, Tower } from '../entities.js';
import type { SimEvent } from '../events.js';
import { angleTo, cos, distSq, sin } from '../math.js';
import { NO_TARGET, type ClassId, type EntityId } from '../types.js';
import { applyDamage } from './damage.js';
import type { SpatialGrid } from './spatial.js';

/** R-06-19. Swarm-6 at wave 25 reaches this, and an uncapped count is a balance failure
 *  as much as a performance one — the screen stops being readable long before it stalls. */
export const MAX_LIVE_DRONES = 80;

export const DRONE_SEEK_RANGE = 6;
export const DRONE_SPEED = 3;
export const DRONE_ORBIT_RADIUS = 0.75;
export const DRONE_ORBIT_RATE = 2;

export interface DroneContext {
  tick: number;
  drones: EntityPool<Drone>;
  enemies: EntityPool<Enemy>;
  towers: EntityPool<Tower>;
  grid: SpatialGrid;
  events: SimEvent[];
  scratch: EntityId[];
  /** Swarm-6, keyed by owning tower. Empty until a board reaches the tier. */
  expiry: ReadonlyMap<EntityId, { classId: ClassId; radiusTiles: number; damage: number }>;
}

/** New summons are skipped at the cap, never queued — a queue only defers the problem. */
export function canSummon(drones: EntityPool<Drone>): boolean {
  return drones.countAlive() < MAX_LIVE_DRONES;
}

/**
 * Advance every drone: seek, close, strike, or orbit.
 *
 * A drone that reaches an enemy deals its damage and dies. Damage goes through the same
 * pipeline as everything else, so a drone kill is tombstoned here and resolved by system
 * 10 like any other — drones cannot award a bounty twice.
 */
export function stepDrones(ctx: DroneContext): void {
  const items = ctx.drones.items;

  for (let i = 0; i < items.length; i++) {
    const d = items[i] as Drone;
    if (!d.alive) continue;

    if (ctx.tick >= d.expiresAtTick) {
      detonate(ctx, d);
      ctx.drones.kill(d);
      continue;
    }

    // A drone outlives its owner only until the next tick — R-06-20.
    const owner = ctx.towers.get(d.ownerTower);
    if (!owner) {
      ctx.drones.kill(d);
      continue;
    }

    let target = ctx.enemies.get(d.targetId);
    if (!target) {
      target = nearestEnemy(d, ctx);
      d.targetId = target ? target.id : NO_TARGET;
    }

    if (!target) {
      // Nothing in reach: hold station around the owner. The angle advances rather than
      // being derived from `tick`, so a drone summoned mid-wave does not snap to a
      // position determined by absolute time.
      d.orbitAngle += DRONE_ORBIT_RATE * DT;
      d.x = owner.x + cos(d.orbitAngle) * DRONE_ORBIT_RADIUS;
      d.y = owner.y + sin(d.orbitAngle) * DRONE_ORBIT_RADIUS;
      continue;
    }

    const step = DRONE_SPEED * DT;
    const reach = target.size + 0.1;
    if (distSq(d.x, d.y, target.x, target.y) <= reach * reach) {
      applyDamage(
        target,
        { amount: d.damage, kind: 'magic', crit: false, tower: owner },
        ctx.tick,
        ctx.events,
      );
      // Swarm-6 detonates here too, sparing the target that already took the full strike.
      detonate(ctx, d, target.id);
      ctx.drones.kill(d);
      continue;
    }

    const angle = angleTo(d.x, d.y, target.x, target.y);
    d.x += cos(angle) * step;
    d.y += sin(angle) * step;
  }
}

function nearestEnemy(d: Drone, ctx: DroneContext): Enemy | undefined {
  ctx.grid.query(d.x, d.y, DRONE_SEEK_RANGE, ctx.enemies, ctx.scratch);

  let best: Enemy | undefined;
  let bestDist = 0;
  for (let i = 0; i < ctx.scratch.length; i++) {
    const candidate = ctx.enemies.get(ctx.scratch[i] as EntityId);
    if (!candidate) continue;
    const dist = distSq(d.x, d.y, candidate.x, candidate.y);
    // Ties on lowest id, as everywhere else that picks among entities (TRD-05 R-05-05).
    if (!best || dist < bestDist || (dist === bestDist && candidate.id < best.id)) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Swarm-6 — a drone detonates where it dies, on impact or on timeout.
 *
 * On impact deliberately, and this was measured rather than assumed. Authored as
 * expiry-only the tier fired 6-8 times a run and dealt EXACTLY ZERO damage every time,
 * because the two conditions are mutually exclusive: a drone only reaches its timeout
 * when nothing has been within its 6-tile seek range for twelve seconds, so nothing is
 * ever inside the 1.5-tile blast when it goes off. Implementing it faithfully to the
 * original wording produced a capstone that looked wired and still did nothing.
 *
 * The struck target is excluded — it already took the full strike — so this is a clean
 * splash on the neighbours rather than a stealth doubling of single-target damage.
 *
 * Attributed to the owning tower so the damage lands on the right leaderboard row, and
 * silent when the owner is gone: a detonation with no source cannot be credited, and
 * crediting it to nobody would make the board summary stop adding up.
 */
function detonate(ctx: DroneContext, drone: Drone, exclude?: EntityId): void {
  const spec = ctx.expiry.get(drone.ownerTower);
  if (!spec) return;
  const owner = ctx.towers.get(drone.ownerTower);
  if (!owner) return;

  ctx.grid.query(drone.x, drone.y, spec.radiusTiles, ctx.enemies, ctx.scratch);
  for (const id of ctx.scratch) {
    if (id === exclude) continue;
    const enemy = ctx.enemies.get(id);
    if (!enemy || !enemy.alive) continue;
    applyDamage(
      enemy,
      { amount: spec.damage, kind: 'magic', crit: false, tower: owner },
      ctx.tick,
      ctx.events,
    );
    if (!enemy.alive) enemy.lastHitBy = owner.id;
  }

  // The renderer never reads world state to answer "where did that happen" (TRD-00 §7),
  // so the position travels with the event.
  ctx.events.push({ t: 'synergyProc', classId: spec.classId, tier: 6, x: drone.x, y: drone.y });
}
